/**
 * AccessLogPage — 访问日志 / 流量分析
 * Route: /admin/access-log
 * API:
 *   GET /api/v1/analytics/summary   — 7 天 PV/UV + 国家 + 热门路径
 *   GET /api/v1/analytics/pageviews — 分页原始访问记录
 */

import { useEffect, useState, useCallback } from 'react';
import { api } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  RefreshCw, Loader2, Globe, Monitor, Smartphone, Tablet,
  BarChart3, MapPin, Route, ChevronLeft, ChevronRight, User,
} from 'lucide-react';
import { cn } from '@/lib/utils';

// ── Types ────────────────────────────────────────────────────────────────────

interface SummaryData {
  period_days: number;
  by_app: Array<{ app: string; pv: string; uv: string }>;
  by_country: Array<{ country: string; app: string; pv: string }>;
  top_paths: Array<{ app: string; path: string; pv: string }>;
}

interface PageView {
  id: string;
  app: string;
  path: string;
  full_url?: string;
  user_display_name?: string;
  ip_address?: string;
  cf_country?: string;
  cf_city?: string;
  device_type?: string;
  client_tz?: string;
  session_id?: string;
  created_at: string;
}

interface PageviewsResponse {
  ok: boolean;
  data: PageView[];
  meta: { total: number; page: number; limit: number; pages: number };
}

// ── Helpers ───────────────────────────────────────────────────────────────────

const APP_COLORS: Record<string, string> = {
  'web-client':   'bg-blue-100 text-blue-800',
  'go-pwa':       'bg-green-100 text-green-800',
  'landing':      'bg-purple-100 text-purple-800',
  'network':      'bg-orange-100 text-orange-800',
  'extension':    'bg-yellow-100 text-yellow-800',
  'self-service': 'bg-pink-100 text-pink-800',
};

function appBadge(app: string) {
  return (
    <span className={cn('text-xs font-medium px-2 py-0.5 rounded-full whitespace-nowrap', APP_COLORS[app] ?? 'bg-gray-100 text-gray-700')}>
      {app}
    </span>
  );
}

function DeviceIcon({ type }: { type?: string }) {
  if (type === 'mobile')    return <Smartphone className="w-3.5 h-3.5 text-muted-foreground" />;
  if (type === 'tablet')    return <Tablet className="w-3.5 h-3.5 text-muted-foreground" />;
  if (type === 'extension') return <Globe className="w-3.5 h-3.5 text-muted-foreground" />;
  return <Monitor className="w-3.5 h-3.5 text-muted-foreground" />;
}

function timeAgo(iso: string) {
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1)   return 'just now';
  if (m < 60)  return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24)  return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

// ── Summary Cards ─────────────────────────────────────────────────────────────

function SummarySection({ summary }: { summary: SummaryData }) {
  const totalPv = summary.by_app.reduce((s, r) => s + Number(r.pv), 0);
  const totalUv = summary.by_app.reduce((s, r) => s + Number(r.uv), 0);

  return (
    <div className="space-y-4">
      {/* Top KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card>
          <CardContent className="pt-4">
            <p className="text-xs text-muted-foreground">Total PV (7d)</p>
            <p className="text-2xl font-bold">{totalPv.toLocaleString()}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <p className="text-xs text-muted-foreground">Unique Sessions (7d)</p>
            <p className="text-2xl font-bold">{totalUv.toLocaleString()}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <p className="text-xs text-muted-foreground">Apps Active</p>
            <p className="text-2xl font-bold">{summary.by_app.length}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <p className="text-xs text-muted-foreground">Countries</p>
            <p className="text-2xl font-bold">
              {new Set(summary.by_country.map(r => r.country)).size || '—'}
            </p>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {/* By App */}
        <Card>
          <CardHeader className="pb-2 pt-4">
            <CardTitle className="text-sm flex items-center gap-1.5"><BarChart3 className="w-4 h-4" /> By App</CardTitle>
          </CardHeader>
          <CardContent className="space-y-1.5">
            {summary.by_app.map(r => (
              <div key={r.app} className="flex items-center justify-between">
                {appBadge(r.app)}
                <span className="text-xs text-muted-foreground">{Number(r.pv).toLocaleString()} PV · {Number(r.uv).toLocaleString()} UV</span>
              </div>
            ))}
            {summary.by_app.length === 0 && <p className="text-xs text-muted-foreground">No data yet</p>}
          </CardContent>
        </Card>

        {/* By Country */}
        <Card>
          <CardHeader className="pb-2 pt-4">
            <CardTitle className="text-sm flex items-center gap-1.5"><MapPin className="w-4 h-4" /> By Country</CardTitle>
          </CardHeader>
          <CardContent className="space-y-1.5 max-h-48 overflow-y-auto">
            {summary.by_country.slice(0, 15).map((r, i) => (
              <div key={i} className="flex items-center justify-between text-xs">
                <span className="font-medium">{r.country || '—'}</span>
                <span className="text-muted-foreground">{Number(r.pv)} · {appBadge(r.app)}</span>
              </div>
            ))}
            {summary.by_country.length === 0 && <p className="text-xs text-muted-foreground">No geo data (local/dev)</p>}
          </CardContent>
        </Card>

        {/* Top Paths */}
        <Card>
          <CardHeader className="pb-2 pt-4">
            <CardTitle className="text-sm flex items-center gap-1.5"><Route className="w-4 h-4" /> Top Paths</CardTitle>
          </CardHeader>
          <CardContent className="space-y-1.5 max-h-48 overflow-y-auto">
            {summary.top_paths.slice(0, 15).map((r, i) => (
              <div key={i} className="flex items-center justify-between text-xs gap-2">
                <span className="font-mono truncate max-w-[140px]" title={r.path}>{r.path}</span>
                <div className="flex items-center gap-1.5 shrink-0">
                  <span className="text-muted-foreground">{Number(r.pv)}</span>
                  {appBadge(r.app)}
                </div>
              </div>
            ))}
            {summary.top_paths.length === 0 && <p className="text-xs text-muted-foreground">No data yet</p>}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

// ── Raw Log Table ─────────────────────────────────────────────────────────────

function LogTable({
  rows, meta, page, onPageChange, loading,
}: {
  rows: PageView[];
  meta: PageviewsResponse['meta'];
  page: number;
  onPageChange: (p: number) => void;
  loading: boolean;
}) {
  return (
    <Card>
      <CardHeader className="pb-2 pt-4 flex-row items-center justify-between">
        <CardTitle className="text-sm">Raw Access Log</CardTitle>
        <span className="text-xs text-muted-foreground">{meta.total.toLocaleString()} entries</span>
      </CardHeader>
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b bg-muted/40">
                <th className="text-left px-3 py-2 font-medium text-muted-foreground">Time</th>
                <th className="text-left px-3 py-2 font-medium text-muted-foreground">App</th>
                <th className="text-left px-3 py-2 font-medium text-muted-foreground">Path</th>
                <th className="text-left px-3 py-2 font-medium text-muted-foreground">Who</th>
                <th className="text-left px-3 py-2 font-medium text-muted-foreground">IP</th>
                <th className="text-left px-3 py-2 font-medium text-muted-foreground">Country</th>
                <th className="text-left px-3 py-2 font-medium text-muted-foreground">Device</th>
                <th className="text-left px-3 py-2 font-medium text-muted-foreground">TZ</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={8} className="text-center py-6 text-muted-foreground">Loading…</td></tr>
              )}
              {!loading && rows.map(r => (
                <tr key={r.id} className="border-b hover:bg-muted/20 transition-colors">
                  <td className="px-3 py-1.5 whitespace-nowrap text-muted-foreground" title={r.created_at}>
                    {timeAgo(r.created_at)}
                  </td>
                  <td className="px-3 py-1.5">{appBadge(r.app)}</td>
                  <td className="px-3 py-1.5 font-mono max-w-[200px] truncate" title={r.full_url ?? r.path}>
                    {r.path}
                  </td>
                  <td className="px-3 py-1.5">
                    {r.user_display_name
                      ? <span className="flex items-center gap-1"><User className="w-3 h-3" />{r.user_display_name}</span>
                      : <span className="text-muted-foreground">—</span>
                    }
                  </td>
                  <td className="px-3 py-1.5 font-mono text-muted-foreground">{r.ip_address ?? '—'}</td>
                  <td className="px-3 py-1.5">
                    {r.cf_country
                      ? <span>{r.cf_country}{r.cf_city ? ` · ${r.cf_city}` : ''}</span>
                      : <span className="text-muted-foreground">—</span>
                    }
                  </td>
                  <td className="px-3 py-1.5"><DeviceIcon type={r.device_type} /></td>
                  <td className="px-3 py-1.5 text-muted-foreground">{r.client_tz ?? '—'}</td>
                </tr>
              ))}
              {!loading && rows.length === 0 && (
                <tr><td colSpan={8} className="text-center py-6 text-muted-foreground">No records</td></tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {meta.pages > 1 && (
          <div className="flex items-center justify-between px-3 py-2 border-t">
            <span className="text-xs text-muted-foreground">
              Page {meta.page} of {meta.pages}
            </span>
            <div className="flex gap-1">
              <Button size="icon" variant="ghost" className="h-7 w-7"
                disabled={page <= 1} onClick={() => onPageChange(page - 1)}>
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Button size="icon" variant="ghost" className="h-7 w-7"
                disabled={page >= meta.pages} onClick={() => onPageChange(page + 1)}>
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export default function AccessLogPage() {
  const [summary, setSummary]       = useState<SummaryData | null>(null);
  const [pvResp, setPvResp]         = useState<PageviewsResponse | null>(null);
  const [page, setPage]             = useState(1);
  const [appFilter, setAppFilter]   = useState<string>('');
  const [days, setDays]             = useState(7);
  const [loading, setLoading]       = useState(false);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [error, setError]           = useState<string | null>(null);

  const loadSummary = useCallback(async () => {
    setSummaryLoading(true);
    try {
      const res = await api.get<{ ok: boolean; data: SummaryData }>('/api/v1/analytics/summary');
      setSummary(res.data);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load summary');
    } finally {
      setSummaryLoading(false);
    }
  }, []);

  const loadPageviews = useCallback(async (p: number) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: String(p), limit: '50', days: String(days),
        ...(appFilter ? { app: appFilter } : {}),
      });
      const res = await api.get<PageviewsResponse>(`/api/v1/analytics/pageviews?${params}`);
      setPvResp(res);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load pageviews');
    } finally {
      setLoading(false);
    }
  }, [appFilter, days]);

  useEffect(() => { loadSummary(); }, [loadSummary]);
  useEffect(() => { setPage(1); loadPageviews(1); }, [loadPageviews]);

  const handlePageChange = (p: number) => { setPage(p); loadPageviews(p); };

  const APP_OPTIONS = ['', 'web-client', 'go-pwa', 'landing', 'network', 'extension', 'self-service'];
  const DAY_OPTIONS = [1, 7, 14, 30];

  return (
    <div className="p-4 md:p-6 space-y-5 max-w-screen-xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-xl font-semibold">Access Log</h1>
          <p className="text-sm text-muted-foreground">跨平台访问日志 · IP 追踪 · 地理分布</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {/* App filter */}
          <select
            value={appFilter}
            onChange={e => setAppFilter(e.target.value)}
            className="h-8 text-xs border rounded px-2 bg-background"
          >
            {APP_OPTIONS.map(a => <option key={a} value={a}>{a || 'All apps'}</option>)}
          </select>
          {/* Days filter */}
          <select
            value={days}
            onChange={e => setDays(Number(e.target.value))}
            className="h-8 text-xs border rounded px-2 bg-background"
          >
            {DAY_OPTIONS.map(d => <option key={d} value={d}>{d}d</option>)}
          </select>
          <Button size="sm" variant="outline" onClick={() => { loadSummary(); loadPageviews(1); }}>
            <RefreshCw className={cn('w-3.5 h-3.5 mr-1', (loading || summaryLoading) && 'animate-spin')} />
            Refresh
          </Button>
        </div>
      </div>

      {error && (
        <div className="text-sm text-destructive bg-destructive/10 rounded px-3 py-2">{error}</div>
      )}

      {/* Summary */}
      {summaryLoading && !summary && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading summary…
        </div>
      )}
      {summary && <SummarySection summary={summary} />}

      {/* Log Table */}
      {pvResp && (
        <LogTable
          rows={pvResp.data}
          meta={pvResp.meta}
          page={page}
          onPageChange={handlePageChange}
          loading={loading}
        />
      )}
      {!pvResp && loading && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading logs…
        </div>
      )}
    </div>
  );
}
